import type {
  AcceptTerms,
  AddToOrgDto,
  MachineSupaDto,
} from "@strtmn/sds/types"
import { getErrorMessage } from "@strtmn/strtmn"
import { Cache } from "aws-amplify/utils"
import { isDev, isProd } from "../config"
import { isUserSignedIn } from "../Utils.AmplifyAuth"
import { clearCacheOrgData } from "./OrgsApi"
import { clearCacheCurrentUserData } from "./userData"
import { getCacheKey, getFromApi, postToApi, putToApi } from "./utils"

// 10 minutes
const EXPIRES_IN_MS = 10 * 60 * 1000

export const createMachine = async (
  machineData: MachineSupaDto
): Promise<boolean | false> => {
  console.log(`> createMachine()`, machineData)
  const apiName = "frontendApi"
  const path = `/machines`

  if (!(await isUserSignedIn())) {
    return false
  }

  try {
    const response = await postToApi(apiName, path, machineData)
    !isProd && console.debug("✅ createMachine: api response:", response)
  } catch (err) {
    console.error(`createMachine: Error creating machine.`)
    console.log("createMachine: Error:", err)
    throw err
  }
  return true
}

export const updateMachine = async (
  machineId: string,
  machineData: MachineSupaDto
): Promise<boolean> => {
  console.log("> updateMachine")
  const apiName = "frontendApi"
  const path = `/machines/${machineId}/settings`

  if (!(await isUserSignedIn())) {
    return false
  }

  try {
    const response = await putToApi(apiName, path, machineData)
    !isProd && console.debug("✅ updateMachine: api response:", response)
  } catch (err) {
    console.error(`updateMachine: Error updating machine for ${machineId}.`)
    console.log("updateMachine: Error:", err)
    throw err
  }
  await clearCacheMachineData(machineId)
  return true
}

export const addMachineToOrg = async (
  machineId: string,
  orgId: string
): Promise<boolean | false> => {
  console.log(`> addMachineToOrg(${machineId}, ${orgId})`)
  const apiName = "frontendApi"
  const path = `/machines/${machineId}/orgs`
  const body: AddToOrgDto = { orgId }

  if (!(await isUserSignedIn())) {
    return false
  }

  try {
    const response = await postToApi(apiName, path, body)
    !isProd && console.debug("✅ addMachineToOrg: api response:", response)
  } catch (err) {
    console.error(
      `addMachineToOrg: Error adding machine ${machineId} to org ${orgId}.`
    )
    console.log("addMachineToOrg: Error:", err)
    throw err
  }
  await clearCacheMachineData(machineId)
  await clearCacheOrgData(orgId)
  return true
}

/**
 * Self-service: links the machine with the given VIN to the org, and accepts
 * the EU Data Act terms for it.
 * @throws the API error, with `body.code` from `MachineErrorCodes`
 */
export const linkMachineToOwnOrg = async (
  orgId: string,
  data: AcceptTerms
): Promise<boolean> => {
  console.log(`> linkMachineToOwnOrg(${orgId})`)
  if (!(await isUserSignedIn())) {
    return false
  }
  try {
    await postToApi("frontendApi", `/orgs/${orgId}/machines`, data)
  } catch (err) {
    console.error(`linkMachineToOwnOrg: Error linking machine to ${orgId}.`)
    console.log("linkMachineToOwnOrg: Error:", err)
    throw err
  }
  await clearCacheOrgData(orgId)
  await clearCacheCurrentUserData()
  return true
}

export const listMachines = async (): Promise<MachineSupaDto[] | false> => {
  console.log(`> listMachines()`)
  const apiName = "frontendApi"
  const path = `/machines`
  const cacheKey = getCacheKey(apiName, path)

  if (!(await isUserSignedIn())) {
    return false
  }

  try {
    // !isProd && console.debug('try to get item from cache')
    console.warn("remove item from cache") // DBG
    await Cache.removeItem(cacheKey) // DBG
    let data: MachineSupaDto[] | null = await Cache.getItem(cacheKey)
    if (data) {
      console.log("listMachines: Found in cache:", data, `${cacheKey}`)
    } else {
      console.log(
        "listMachines:",
        cacheKey,
        "not found in cache. Loading from API..."
      )
      data = (await getFromApi(apiName, path)).json as MachineSupaDto[]
      await Cache.setItem(cacheKey, data, {
        expires: new Date().getTime() + EXPIRES_IN_MS,
      })
      !isProd && console.debug("listMachines: Fetched from api:", data)
    }

    // !isProd && console.debug("✅ listMachines: data")
    return data
  } catch (err) {
    console.error(
      "listMachines: Error fetching Machines data from cache (unlikely) or api (likely)."
    )
    console.log("listMachines: Error:", err)
    if (isDev) {
      const errMsg = getErrorMessage(err)
      if (errMsg.includes("NetworkError")) {
        const apiMsg = `API call was to ${apiName} ${path}`
        console.log(apiMsg)
        const errAsError = err as Error
        if ("body" in errAsError) {
          errAsError.body += "\n" + apiMsg
        }
      }
    }
    throw err
  }
}

export const getById = async (
  machineId: string
): Promise<MachineSupaDto | false> => {
  console.log(`> getById()`)
  const apiName = "frontendApi"
  const path = `/machines/${machineId}`
  const cacheKey = getCacheKey(apiName, path)

  if (!(await isUserSignedIn())) {
    return false
  }

  try {
    // !isProd && console.debug("try to get item from cache")
    console.warn("remove item from cache") // DBG
    await Cache.removeItem(cacheKey) // DBG
    let data: MachineSupaDto | null = await Cache.getItem(cacheKey)
    if (data) {
      console.log("getById: Found in cache:", data, `${cacheKey}`)
    } else {
      console.log(
        "getById:",
        cacheKey,
        "not found in cache. Loading from API..."
      )
      data = (await getFromApi(apiName, path)).json as MachineSupaDto
      await Cache.setItem(cacheKey, data, {
        expires: new Date().getTime() + EXPIRES_IN_MS,
      })
      !isProd && console.debug("getById: Fetched from api:", data)
    }

    // !isProd && console.debug("✅ getById: data")
    return data
  } catch (err) {
    console.error(
      "getById: Error fetching Machine data from cache (unlikely) or api (likely)."
    )
    console.log("machines.getById: Error:", err)
    throw err
  }
}

export const getLatestActiveDnv = (machine: MachineSupaDto) => {
  console.log("> getLatestActiveDnv")
  // console.log("machine:", machine)
  if (!machine.hasActiveDnv) {
    console.warn(
      "Machine has no active DNV: hasActiveDnv=",
      machine.hasActiveDnv
    )
    return null
  }
  const activeTerms = machine.EuDataActTerms?.filter(
    (t) => t.cancel_date === null
  )
  if (!activeTerms || activeTerms.length === 0) {
    console.warn("Machine has no active DNV (empty list)")
    return null
  }
  let latestActiveTerms = null
  for (const t of activeTerms) {
    if (!latestActiveTerms) {
      console.log("Setting latest active DNV for the first time:", t)
      latestActiveTerms = t
      continue
    }
    if (t.terms_version > latestActiveTerms.terms_version) {
      console.log("Updating latest active DNV:", t)
      latestActiveTerms = t
      continue
    }
  }
  return latestActiveTerms
}

export const sortMachines = (machines: MachineSupaDto[]) => {
  console.debug(`> sortMachines (${machines.length} machines)`)
  const pushM1ToBack = 1
  const pushM2ToBack = -1
  // machines.forEach((o: MachineSupaDto) => {
  //   o.adresseKurz =
  //     o.adresse && o.adresse.ort && o.adresse.strasse
  //       ? `${o.adresse.ort}, ${o.adresse.strasse}`
  //       : undefined
  // })
  // console.log('res: ', items)
  machines.sort((m1: MachineSupaDto, m2: MachineSupaDto) => {
    // Sort by name
    if (m1.name < m2.name) {
      return pushM2ToBack
    }
    if (m1.name > m2.name) {
      return pushM1ToBack
    }
    // Sort by serial (VIN)
    if (m1.serial_number < m2.serial_number) {
      return pushM2ToBack
    }
    if (m1.serial_number > m2.serial_number) {
      return pushM1ToBack
    }

    return 0
  })
  return machines
}

export const filterMachines = (
  machines: MachineSupaDto[],
  filter: string | undefined
) => {
  if (!filter || !machines) {
    return machines
  }
  return machines.filter((machine) => {
    const haystack = [
      machine.serial_number,
      machine.modem_serial,
      machine.name,
      machine.machine_type,
      machine.machine_attributes,
    ]
      .join("#")
      .toLowerCase()
    return haystack.includes(filter)
  })
}

export const clearCacheMachineData = async (
  machineId: string
): Promise<void> => {
  console.log(`> clearCacheMachineData(${machineId})`)
  const apiName = "frontendApi"
  const path = `/machines/${machineId}`
  const cacheKey = getCacheKey(apiName, path)
  !isProd && console.debug("Remove item from cache:", cacheKey)
  await Cache.removeItem(cacheKey)
}
